import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Plus, CalendarPlus, ListTodo } from "lucide-react";
import { addMonths, format, subMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarGrid, eventsOnDate } from "@/components/calendar/CalendarGrid";
import { EventModal } from "@/components/calendar/EventModal";
import { DayEventsModal } from "@/components/calendar/DayEventsModal";
import { CalendarSidebarExtra } from "@/components/calendar/CalendarSidebarExtra";
import { CalendarEvent } from "@/components/calendar/types";
import {
  loadEvents,
  saveEvent,
  deleteEvent as deleteEventRemote,
  migrateLocalStorageIfNeeded,
} from "@/components/calendar/storage";
import { loadTodos, TODOS_CHANGED_EVENT } from "@/components/todo/storage";
import { Todo } from "@/components/todo/types";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export default function Calendario() {
  const navigate = useNavigate();
  const [monthDate, setMonthDate] = useState(() => new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<CalendarEvent> | null>(null);
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [dayModalDate, setDayModalDate] = useState<Date | null>(null);
  const [chooserDate, setChooserDate] = useState<Date | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      const uid = data.user?.id;
      if (!uid) {
        navigate("/auth");
        return;
      }
      if (cancelled) return;
      setUserId(uid);
      try {
        const tlist = await loadTodos();
        if (!cancelled) setTodos(tlist);
      } catch {
        /* ignore */
      }
      try {
        const migrated = await migrateLocalStorageIfNeeded();
        if (migrated > 0) {
          toast.success(`Eventos migrados para a nuvem (${migrated})`);
        }
      } catch {
        /* silent — will retry next load */
      }
      try {
        const list = await loadEvents();
        if (!cancelled) setEvents(list);
      } catch (e: any) {
        toast.error("Erro ao carregar eventos");
      }
    })();
    const syncTodos = () => {
      loadTodos().then((list) => setTodos(list)).catch(() => {});
    };
    window.addEventListener(TODOS_CHANGED_EVENT, syncTodos);
    window.addEventListener("storage", syncTodos);
    return () => {
      cancelled = true;
      window.removeEventListener(TODOS_CHANGED_EVENT, syncTodos);
      window.removeEventListener("storage", syncTodos);
    };
  }, [navigate]);

  const todosToEvents = (list: Todo[]): CalendarEvent[] =>
    list
      .filter((t) => !!t.date)
      .map((t) => ({
        id: `todo:${t.id}`,
        title: `${t.completed ? "✓" : "📋"} ${t.title}`,
        startDate: t.date!,
        endDate: t.date!,
        startTime: "00:00",
        endTime: "23:59",
        color: t.color,
      }));

  const mergedEvents = useMemo(
    () => [...events, ...todosToEvents(todos)],
    [events, todos],
  );

  const handleDayClick = (date: Date) => {
    setChooserDate(date);
  };

  const handleEventClick = (ev: CalendarEvent) => {
    if (ev.id.startsWith("todo:")) {
      navigate(`/todo?edit=${ev.id.slice(5)}`);
      return;
    }
    setEditing(ev);
    setModalOpen(true);
  };

  const handleSave = async (ev: CalendarEvent) => {
    if (!userId) return;
    try {
      const saved = await saveEvent(ev);
      setEvents((prev) => {
        const idx = prev.findIndex((e) => e.id === saved.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = saved;
          return next;
        }
        return [...prev, saved];
      });
    } catch (e: any) {
      toast.error("Erro ao salvar evento");
    }
  };

  const handleDelete = async (id: string) => {
    if (!userId) return;
    try {
      await deleteEventRemote(id);
      setEvents((prev) => prev.filter((e) => e.id !== id));
    } catch (e: any) {
      toast.error("Erro ao excluir evento");
    }
  };

  const handleShowMore = (date: Date) => {
    setDayModalDate(date);
    setDayModalOpen(true);
  };

  const dayModalEvents = useMemo(
    () => (dayModalDate ? eventsOnDate(mergedEvents, dayModalDate) : []),
    [mergedEvents, dayModalDate],
  );

  const monthLabel = format(monthDate, "MMMM 'de' yyyy", { locale: ptBR });

  return (
    <AppShell>
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] mx-auto">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground capitalize">
              {monthLabel}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {events.length} {events.length === 1 ? "evento salvo" : "eventos salvos"}
            </p>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setMonthDate(new Date())}>
              Hoje
            </Button>
            <div className="flex items-center rounded-xl border border-border bg-card overflow-hidden">
              <button
                onClick={() => setMonthDate((d) => subMonths(d, 1))}
                className="h-9 w-9 flex items-center justify-center hover:bg-muted transition-colors"
                aria-label="Mês anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setMonthDate((d) => addMonths(d, 1))}
                className="h-9 w-9 flex items-center justify-center hover:bg-muted transition-colors border-l border-border"
                aria-label="Próximo mês"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <Button
              size="sm"
              onClick={() => {
                const iso = format(new Date(), "yyyy-MM-dd");
                setEditing({ startDate: iso, endDate: iso, startTime: "09:00", endTime: "10:00", color: "blue" });
                setModalOpen(true);
              }}
            >
              <Plus className="h-4 w-4" /> Novo evento
            </Button>
          </div>
        </div>

        {/* Grid + sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5">
          <CalendarGrid
            monthDate={monthDate}
            events={mergedEvents}
            onDayClick={handleDayClick}
            onEventClick={handleEventClick}
            onShowMore={handleShowMore}
          />
          <CalendarSidebarExtra />
        </div>
      </div>

      <EventModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        initial={editing}
        onSave={handleSave}
        onDelete={handleDelete}
      />
      <DayEventsModal
        open={dayModalOpen}
        onOpenChange={setDayModalOpen}
        date={dayModalDate}
        events={dayModalEvents}
        onSelect={(ev) => {
          if (ev.id.startsWith("todo:")) {
            setDayModalOpen(false);
            navigate(`/todo?edit=${ev.id.slice(5)}`);
            return;
          }
          setEditing(ev);
          setModalOpen(true);
        }}
      />

      <Dialog open={!!chooserDate} onOpenChange={(o) => !o && setChooserDate(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>
              {chooserDate ? format(chooserDate, "d 'de' MMMM", { locale: ptBR }) : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Button
              className="w-full justify-start"
              variant="outline"
              onClick={() => {
                if (!chooserDate) return;
                const iso = format(chooserDate, "yyyy-MM-dd");
                setChooserDate(null);
                setEditing({ startDate: iso, endDate: iso, startTime: "09:00", endTime: "10:00", color: "blue" });
                setModalOpen(true);
              }}
            >
              <CalendarPlus className="h-4 w-4" /> Novo evento
            </Button>
            <Button
              className="w-full justify-start"
              variant="outline"
              onClick={() => {
                if (!chooserDate) return;
                const iso = format(chooserDate, "yyyy-MM-dd");
                setChooserDate(null);
                navigate(`/todo?new=${iso}`);
              }}
            >
              <ListTodo className="h-4 w-4" /> Nova tarefa
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}